import { useCallback, useEffect, useMemo, useState } from 'react'

import { capitalize } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useInfiniteQuery, useQueryClient } from 'react-query'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import { IconPlusInBox } from 'assets/svg/sprite'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import { AddCreatorsToList } from 'components-ref/organisms/modals/list/addCreatorsToList'
import { usePageTitle } from 'context/pageTitleContext'
import CreatorsList from 'features/CreatorsList/CreatorsList'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import { AllIcons } from 'helpers/allIcons'
import { useFilterQuery } from 'helpers/hooks'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import './_subscriptions.scss'
import { getSubscriptionList } from 'services/endpoints/subscriptionList'
import { removeUsersFromSubscriptionList } from 'services/endpoints/subscriptionList'
import { useSubscriptionListCacheStore } from 'store/subscriptionListCacheStore'
import colors from 'styles/colors'

const useSortSubsActive = () => {
    const { t } = useTranslation()

    return [
        { value: 1, name: t('recentlySubscribed') },
        { value: 2, name: t('favorite') },
        { value: 3, name: t('expiring') },
        { value: 4, name: t('onlineStatus') },
        { value: 5, name: t('lastActivity') },
        { value: 6, name: t('name') }
    ]
}

const useSortSubsExpired = () => {
    const { t } = useTranslation()

    return [
        { value: 1, name: t('recentlyExpired') },
        { value: 2, name: t('favorite') },
        { value: 3, name: t('expiring') },
        { value: 4, name: t('name') }
    ]
}

const useSubsOrder = () => {
    const { t } = useTranslation()

    return [
        { value: 1, name: t('ascending') },
        { value: 2, name: t('descending') }
    ]
}

const mapSubscriptionResourceToCreator = (item: any, activeFilter: 'active' | 'expired') => {
    const data = item?.model_data ?? item

    return {
        coverUrl: data.cover,
        avatarUrl: data.avatar,
        name: data.name,
        handle: data.username,

        avatar: data.avatar,
        cover: data.cover,
        username: data.username,
        user_id: data.user_id,
        is_friend: data.is_friend ?? false,
        is_subscribed: data.is_subscribed ?? true,

        isLive: !!data.active_stream,
        isOnline: !!data.is_online,
        isActive: activeFilter === 'active',
        price: data.price,
        current_plan_price: data.current_plan_price,
        save: data.discount,
        userId: data.user_id,
        conversation_id: data.conversation_id,
        newPostsCount: data.new_posts,
        expires: data.expires,
        billingCycle: data.billing_cycle,
        monthsSubscribed: data.total_months_subscribed,
        renewalPrice: data.one_month_price,
        renewDate:
            data.subscription_renew_date === 'no renewal'
                ? ''
                : data.subscription_renew_date,
        lastActivity: data.last_activity
    }
}

export const List = () => {
    const { setTitle } = usePageTitle()
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [selectedSort, setSelectedSort] = useState(1)
    const [selectedOrder, setSelectedOrder] = useState(1)
    const [activeFilter, setActiveFilter] = useState<'active' | 'expired'>('active')
    const [isViewsOpen, setIsViewsOpen] = useState<boolean>(false)
    const queryClient = useQueryClient()
    const [isRemovingCreator, setIsRemovingCreator] = useState(false)

    const navigate = useNavigate()
    const sortSubsActive = useSortSubsActive()
    const sortSubsExpired = useSortSubsExpired()
    const sortSubsOrder = useSubsOrder()
    const { t } = useTranslation()

    const filter = useFilterQuery()
    const type = filter.get('type')
    const sort = filter.get('sort')
    const order = filter.get('order')
    const { state } = useLocation()
    const { id } = useParams()
    const listId = id ?? undefined

    const setCreators = useSubscriptionListCacheStore(state => state.setCreators)

    const [selectedCreatorId, setSelectedCreatorId] = useState<string | null>(null)

    const handleSelectCreator = useCallback((userId: string) => {
        setSelectedCreatorId(prev =>
            prev === userId ? null : userId
        )
    }, [])

    const cacheKey = useMemo(() => {
        return [
            'subscriptionsList',
            listId ?? 'all',
            activeFilter,
            selectedOrder,
            selectedSort
        ].join(':')
    }, [listId, activeFilter, selectedOrder, selectedSort])

    const creators = useSubscriptionListCacheStore(
        state => state.creatorsByKey[cacheKey] ?? []
    )

    const handleRemoveCreator = useCallback(
        async (userId: string) => {
            if (!listId || isRemovingCreator) return

            setIsRemovingCreator(true)

            try {
                await removeUsersFromSubscriptionList(listId, [userId])

                // Remove from visible Zustand cache.
                setCreators(
                    cacheKey,
                    creators.filter(creator => creator.userId !== userId)
                )

                // Close dropdown and clear selection.
                setSelectedCreatorId(null)

                // Refresh list and list counts.
                await Promise.all([
                    queryClient.invalidateQueries(['subscriptionsList', listId]),
                    queryClient.invalidateQueries(['allSubscriptionLists'])
                ])
            } catch (error) {
                console.error('Failed to remove creator from list:', error)
            } finally {
                setIsRemovingCreator(false)
            }
        },
        [
            listId,
            isRemovingCreator,
            cacheKey,
            creators,
            setCreators,
            queryClient
        ]
    )
    
    const { data, isFetching } = useInfiniteQuery(
        [
            'subscriptionsList',
            listId,
            activeFilter,
            selectedOrder,
            selectedSort
        ],
        async ({ pageParam = 1 }) =>
            await getSubscriptionList(
                listId,
                selectedSort,
                activeFilter,
                selectedOrder,
                pageParam
            ),
        {
            getNextPageParam: lastPage => lastPage?.nextCursor,
            enabled: true,
            keepPreviousData: true,
            refetchOnWindowFocus: false
        }
    )

    const listName =
        state?.listName ??
        data?.pages?.[0]?.page?.data?.meta?.list?.name ??
        ''

    useEffect(() => {
        setTitle(t('List'))
    }, [setTitle, t])

    useEffect(() => {
        if (type && (type === 'active' || type === 'expired')) {
            setActiveFilter(type)
        } else {
            setActiveFilter('active')
        }

        if (sort) {
            setSelectedSort(parseInt(sort))
        }

        if (order) {
            setSelectedOrder(parseInt(order))
        }
    }, [type, sort, order])

    useEffect(() => {
        if (!data || isFetching) return

        const mappedCreators = data.pages
            .map(page => page?.page?.data?.data)
            .flat()
            .filter(Boolean)
            .map(item => mapSubscriptionResourceToCreator(item, activeFilter))

        setCreators(cacheKey, mappedCreators)
    }, [isFetching, data, activeFilter, cacheKey, setCreators])

    const handleCreatorsAdded = useCallback(
        (addedItems: any[]) => {
            if (!addedItems?.length) return

            const addedCreators = addedItems.map(item =>
                mapSubscriptionResourceToCreator(item, activeFilter)
            )

            const mergedCreators = [
                ...creators,
                ...addedCreators.filter(
                    addedCreator =>
                        !creators.some(
                            existingCreator =>
                                existingCreator.userId === addedCreator.userId
                        )
                )
            ]

            setCreators(cacheKey, mergedCreators)
        },
        [activeFilter, cacheKey, creators, setCreators]
    )

    const creatorUserIds = creators
    .map(creator => creator.userId)
    .filter(Boolean)

    return (
        <>
            <WithHeaderSection
                customClass='subscription_list'
                headerSection={
                    <LayoutHeader
                        type='search-with-buttons'
                        searchValue={searchTerm}
                        searchFn={(term: string) => {
                            setSearchTerm(term)
                        }}
                        clearFn={() => {
                            setSearchTerm('')
                        }}
                        additionalProps={{
                            placeholder: t('searchSubscriptions')
                        }}
                        buttons={[
                            {
                                type: 'cta',
                                icon: AllIcons.listblue,
                                action: () => {
                                    navigate('/subscriptions')
                                }
                            },
                            {
                                type: 'sort',
                                elements: {
                                    first_section:
                                        activeFilter === 'active'
                                            ? sortSubsActive
                                            : sortSubsExpired,
                                    second_section: sortSubsOrder
                                },
                                selectedSort: { selectedSort },
                                selectedOrder: { selectedOrder }
                            }
                        ]}
                        applyFn={(val: string, val1?: string) => {
                            val && setSelectedSort(parseInt(val))
                            val1 && setSelectedOrder(parseInt(val1))
                        }}
                    />
                }
            >
                {listName && (
                    <Title>
                        <h1>{capitalize(listName)}</h1>

                        <div className='subscriptions__tabs--count'>
                            <span>{creators.length} </span>
                            {t('subscriptions')}
                        </div>
                    </Title>
                )}

                {creators?.length > 0 && (
                    <CreatorsList
                        creators={creators as any}
                        heightAdjustment={320}
                        renderLocation={
                            activeFilter === 'active'
                                ? 'subscriptionsActive'
                                : 'subscriptionsExpired'
                        }
                        listSelection={
                            Boolean(listId) &&
                            activeFilter === 'active' &&
                            listName.toLowerCase() !== 'all'
                        }
                        selectedItems={
                            selectedCreatorId ? [selectedCreatorId] : []
                        }
                        onSelect={handleSelectCreator}
                        onRemoveFromList={handleRemoveCreator}
                    />
                )}

                {activeFilter === 'active' && listName.toLowerCase() !== 'all' && (
                    <>
                        <ActionCard
                            text='Add Creators'
                            addNewText
                            icon={<IconPlusInBox width='22' height='22' />}
                            clickFn={() => {
                                setIsViewsOpen(true)
                            }}
                        />
                    </>
                )}

                {isViewsOpen && (
                    <AddCreatorsToList
                        id={listId}
                        addedCreators={creatorUserIds}
                        onCreatorsAdded={handleCreatorsAdded}
                        onClose={() => setIsViewsOpen(false)}
                    />
                )}
            </WithHeaderSection>
        </>
    )
}

const Title = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 2rem;
    padding-left:0.5rem;
    padding-right:0.5rem;

    h1 {
        font-size: 1.8rem;
        font-weight: 600;
        color: ${colors.blueDark};
        overflow: hidden;
        word-wrap: break-word;
        margin: 0;
    }

    .subscriptions__tabs--count {
        flex-shrink: 0;
    }
`